import { useNavigator } from '@repo/toolkit/use-navigator'
import DataTable, { Column } from '@repo/ui/data-table'
import Typography from '@repo/ui/typography'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { getCenterTeachingHalls } from '../../../../../api/centers/center-teaching-hall/get-center-teaching-hall'
import { useObserver } from '../../../../../lib/contexts/observer'
import useTranslate from '../../../../../lib/hooks/use-translate'
import { TeachingHall } from '../../../../../lib/types/models/ol/teaching-hall/teaching-hall'
import { getTeachingHallColumns } from '../../../../../views/ol-personal-space/teaching-halls/utils'

type Props = {
  centerId: number
  navigationPath?: (t: TeachingHall) => string | undefined
  extraColumns?: Column<TeachingHall>[]
}

const CenterTeachingHallTable = ({ centerId, extraColumns, navigationPath }: Props) => {
  const { t } = useTranslate()
  const { navigate } = useNavigator()
  const queryClient = useQueryClient()

  const { data: centerTeachingHalls } = useQuery({
    queryKey: ['center-teaching-halls', centerId],
    queryFn: () => getCenterTeachingHalls(centerId)
  })

  useObserver({
    key: 'ol-teaching-hall-table',
    update: () => {
      queryClient.invalidateQueries({ queryKey: ['center-teaching-halls', centerId] })
      queryClient.invalidateQueries({ queryKey: ['center-remaining-teaching-halls', centerId] })
    }
  })

  const columns = getTeachingHallColumns(t, true).filter((column) => column.accessorKey !== 'name')

  return (
    <div>
      <div className="h-3" />
      <DataTable<TeachingHall>
        bodyVariant="white"
        paginated={false}
        rowCount={centerTeachingHalls?.data.length ?? 0}
        data={centerTeachingHalls?.data ?? []}
        columns={[
          {
            accessorKey: 'name',
            header: t('teaching.hall.table.name'),
            enableSorting: true,
            cell: ({ row }) => {
              if (!navigationPath)
                return <Typography variant="secondary-body">{row.original.name}</Typography>
              return (
                <Typography.Link
                  variant="secondary-body"
                  onClick={(e) => {
                    e.stopPropagation()
                    const path = navigationPath(row.original)
                    if (path) navigate(path)
                  }}
                >
                  {row.original.name}
                </Typography.Link>
              )
            }
          },
          ...columns,
          ...(extraColumns ?? [])
        ]}
      />
    </div>
  )
}

export default CenterTeachingHallTable
